import React, { useState, useEffect } from "react";
import {
  Box, Typography, TextField, Button, Grid,
  FormControl, InputLabel, Select, MenuItem,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useIntl } from "react-intl";
import { createItCategory, fetchItTypes, fetchBusinessUnitsByDomain } from "../../api/companyApi";
import { useNotification } from "../../providers/NotificationProvider";
import { COMPANY_ID } from "../../config/appConfig";

export default function ItCategoryCreatePage() {
  const { businessDomainId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });

  const [itTypes, setItTypes] = useState([]);
  const [selectedItType, setSelectedItType] = useState(null);
  const [businessUnitId, setBusinessUnitId] = useState(null);
  const [form, setForm] = useState({ itCategoryName: "", itCategoryDescription: "", url: "" });

  useEffect(() => {
    (async () => {
      try {
        const [types, units] = await Promise.all([
          fetchItTypes(businessDomainId),
          fetchBusinessUnitsByDomain(businessDomainId),
        ]);
        setItTypes(Array.isArray(types) ? types : []);
        const unit = Array.isArray(units) ? units[0] : null;
        setBusinessUnitId(unit?.businessUnitId || null);
      } catch (e) { notify.apiError(e); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessDomainId]);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleTypeChange = (e) => {
    const selected = itTypes.find((it) => it.itTypeId === e.target.value);
    setSelectedItType(selected || null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await createItCategory({
        companyId: COMPANY_ID,
        businessUnitId,
        itCategoryName: form.itCategoryName,
        itCategoryDescription: form.itCategoryDescription || null,
        url: form.url || null,
        itTypeId: selectedItType?.itTypeId || null,
      });
      notify.success(t("message.create.success"));
      navigate(`/strategy/${businessDomainId}/it-categories`, { replace: true });
    } catch (err) { notify.apiError(err); }
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>{t("itCategory.create.title")}</Typography>
      <Box component="form" onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid size={12}>
            <TextField fullWidth required label={t("itCategory.name")}
              name="itCategoryName" value={form.itCategoryName} onChange={handleChange} />
          </Grid>
          <Grid size={12}>
            <FormControl fullWidth required>
              <InputLabel>{t("itCategory.itType")}</InputLabel>
              <Select value={selectedItType?.itTypeId || ""} onChange={handleTypeChange}
                label={t("itCategory.itType")}>
                {itTypes.map((it) => (
                  <MenuItem key={it.itTypeId} value={it.itTypeId}>{it.itTypeName}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={12}>
            <TextField fullWidth label={t("common.url")}
              name="url" value={form.url} onChange={handleChange} />
          </Grid>
          <Grid size={12}>
            <TextField fullWidth multiline rows={3} label={t("common.description")}
              name="itCategoryDescription" value={form.itCategoryDescription} onChange={handleChange} />
          </Grid>
        </Grid>
        <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
          <Button variant="contained" type="submit">{t("common.add")}</Button>
          <Button variant="outlined" onClick={() => navigate(`/strategy/${businessDomainId}/it-categories`)}>{t("common.cancel")}</Button>
        </Box>
      </Box>
    </Box>
  );
}
